import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getRubricById, updateRubric, deleteRubric } from '@/api/rubrics/api';
import RubricForm from './components/RubricForm';
import type { RubricRequest, KeyValuePair } from '@/api/rubrics/types';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Save, Trash2, AlertCircle, CheckCircle2, Lock, ShieldAlert } from 'lucide-react';
import { itemVariants } from '@/components/animations';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { SiteHeader } from '@/components/site-header';
import { useAuth } from '@/context/AuthContext';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const RubricEditSkeleton = () => {
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information Card */}
                <Card>
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Skeleton className="h-4 w-4" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-3 w-48" />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-24 w-full rounded-lg" />
                        </div>
                    </CardContent>
                </Card>

                {/* Connections Card */}
                <Card>
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Skeleton className="h-4 w-4" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Skeleton className="h-4 w-28" />
                                <Skeleton className="h-3 w-40" />
                            </div>
                            <Skeleton className="h-5 w-5 rounded-full" />
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Custom Evaluation Criteria & Instructions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Custom Fields Card */}
                <Card>
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Skeleton className="h-4 w-4" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Skeleton className="h-4 w-40" />
                                <Skeleton className="h-3 w-56" />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex gap-2">
                            <Skeleton className="h-10 flex-1" />
                            <Skeleton className="h-10 w-28" />
                        </div>
                        {/* Sample Custom Field Cards */}
                        {[1, 2].map((i) => (
                            <Card key={i} className="shadow-sm">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                                <Skeleton className="h-4 w-4" />
                                            </div>
                                            <div className="flex-1 space-y-1">
                                                <Skeleton className="h-4 w-32" />
                                                <Skeleton className="h-3 w-40" />
                                            </div>
                                        </div>
                                        <Skeleton className="h-8 w-8" />
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid gap-4 sm:grid-cols-3">
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-8" />
                                            <Skeleton className="h-10 w-full" />
                                        </div>
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-16" />
                                            <Skeleton className="h-10 w-full" />
                                        </div>
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-10" />
                                            <Skeleton className="h-10 w-full" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </CardContent>
                </Card>

                {/* Instructions Card */}
                <Card>
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Skeleton className="h-4 w-4" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <Skeleton className="h-4 w-28" />
                                <Skeleton className="h-3 w-48" />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <Skeleton className="h-16 w-full" />
                        <div className="space-y-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <Skeleton className="h-5 w-5 rounded-full shrink-0 mt-0.5" />
                                    <div className="space-y-1.5 flex-1">
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-3 w-3/4" />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Skeleton className="h-px w-full" />
                        <div className="space-y-2">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-16 w-full rounded-lg" />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

const RubricEdit = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [initialData, setInitialData] = useState<RubricRequest | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const navigate = useNavigate();
    const { rubricId } = useParams();
    const { error: showError, success } = useEnhancedToast();
    const hasFetched = useRef(false);
    const { user } = useAuth();
    
    const canEditRubric = user?.permissions?.rubrics === 'EDITOR';

    const fetchRubric = useCallback(async () => {
        if (!rubricId) return;
        
        setIsLoading(true);
        try {
            const response = await getRubricById(rubricId);
            
            if (!response.data?.success || !response.data?.data) {
                throw new Error(response.error || 'Could not load rubric data');
            }

            const rubric = response.data.data;
            
            if (!rubric.name || !rubric.type) {
                throw new Error('Invalid rubric data: missing required fields');
            }

            // Extract base fields
            const rubricRequest: RubricRequest = {
                name: rubric.name,
                type: rubric.type,
                assistantIds: rubric.assistantIds || [],
                phoneNumberIds: rubric.phoneNumberIds || [],
                multiAgentIds: rubric.multiAgentIds || [],
                successEvaluation: rubric.successEvaluation || '',
            };

            // Handle custom fields separately
            const excludedFields = [
                'name', 'type', 'organizationId', 'isActive', 'createdAt', 'updatedAt',
                'assistantIds', 'phoneNumberIds', 'multiAgentIds', 'successEvaluation', '_id',
                'createdBy', 'updatedBy', '__v'
            ];

            // Add any remaining fields as custom fields
            Object.entries(rubric).forEach(([key, value]) => {
                if (!excludedFields.includes(key)) {
                    // Ensure the value is an array of KeyValuePair
                    if (Array.isArray(value) && value.length > 0 && 
                        typeof value[0] === 'object' && value[0] !== null &&
                        'key' in value[0] && 'value' in value[0]) {
                        rubricRequest[key] = value as KeyValuePair[];
                    }
                }
            });

            setInitialData(rubricRequest);
        } catch (error) {
            console.error('Error fetching rubric:', error);
            showError({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to fetch rubric details',
                icon: AlertCircle
            });
            navigate('/dashboard/rubrics');
        } finally {
            setIsLoading(false);
        }
    }, [rubricId, showError, navigate]);

    useEffect(() => {
        // Only fetch if we have a rubricId and haven't fetched yet
        if (rubricId && !hasFetched.current && !initialData) {
            hasFetched.current = true;
            fetchRubric();
        }

        return () => {
            // Only reset hasFetched when rubricId changes
            if (!rubricId) {
                hasFetched.current = false;
            }
        };
    }, [rubricId, fetchRubric]);

    const handleSubmit = async (data: RubricRequest) => {
        setIsLoading(true);
        try {
            const response = await updateRubric(rubricId!, data);
            if (response.data) {
                success({
                    title: 'Rubric Updated',
                    description: 'The rubric has been successfully updated.',
                    icon: CheckCircle2
                });
            } else if (response.error) {
                showError({
                    title: 'Update Failed',
                    description: response.error,
                    icon: AlertCircle
                });
            }
        } catch {
            showError({
                title: 'Update Failed',
                description: 'Could not update rubric',
                icon: AlertCircle
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!rubricId) return;
        setIsDeleting(true);
        try {
            const response = await deleteRubric(rubricId);
            if (response.data) {
                success({
                    title: 'Rubric Deleted',
                    description: 'The rubric has been successfully deleted.',
                    icon: CheckCircle2
                });
                setDialogOpen(false);
                navigate('/dashboard/rubrics');
            } else {
                showError({
                    title: 'Deletion Failed',
                    description: 'Could not delete rubric',
                    icon: AlertCircle
                });
            }
        } catch {
            showError({
                title: 'Deletion Failed',
                description: 'Could not delete rubric',
                icon: AlertCircle
            });
        } finally {
            setIsDeleting(false);
        }
    };

    if (!initialData) {
        return <RubricEditSkeleton />;
    }

    return (
        <>
            <SiteHeader 
                title="Edit Rubric"
                subtitle="Modify your rubric settings and criteria."
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            >
                {canEditRubric ? (
                    <>
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="text-destructive hover:text-destructive gap-2"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Delete Rubric</DialogTitle>
                                    <DialogDescription>
                                        This action cannot be undone. All rubric data will be permanently deleted.
                                    </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setDialogOpen(false)}
                                        disabled={isDeleting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={handleDelete}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Deleting
                                            </>
                                        ) : (
                                            'Delete Rubric'
                                        )}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <Button 
                            type="submit"
                            form="rubric-form"
                            disabled={isLoading}
                            size="sm"
                            className="gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    Save Changes
                                </>
                            )}
                        </Button>
                    </>
                ) : (
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted rounded-md">
                                    <Lock className="h-4 w-4" />
                                    View Only
                                </div>
                            </TooltipTrigger>
                            <TooltipContent className="flex items-center gap-2 max-w-xs">
                                <ShieldAlert className="w-8 h-8 text-amber-500 shrink-0" />
                                <p>You have view-only access. Contact your administrator for edit permissions.</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8"
                >
                    <RubricForm 
                        initialData={initialData} 
                        onSubmit={handleSubmit} 
                        formId="rubric-form"
                    />
                </motion.div>
            </div>
        </>
    );
};

export default RubricEdit; 